import React from 'react';
import { Modal, Typography, Divider, Row, Col, Space, Button, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { CashFlow } from '../../types';
import dayjs from 'dayjs';
import { describeRecurrence } from './recurrence';

const { Title, Text } = Typography;

interface CashFlowDetailsProps {
  open: boolean;
  onClose: () => void;
  cashFlow: CashFlow | null;
  onEdit: (cashFlow: CashFlow) => void;
  onDelete: (cashFlowId: string) => void;
}

const CashFlowDetails: React.FC<CashFlowDetailsProps> = ({
  open,
  onClose,
  cashFlow,
  onEdit,
  onDelete,
}) => {
  if (!cashFlow) return null;

  const handleDelete = () => {
    onDelete(cashFlow.id);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={500}
      bodyStyle={{ padding: '24px', maxHeight: '80vh', overflowY: 'auto' }}
      // Removed custom positioning to maintain default modal behavior
      closable
      maskClosable
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Header with Actions */}
        <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4}>Cash Flow Details</Title>
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => onEdit(cashFlow)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this cash flow?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </Space>

        <Divider />

        {/* CashFlow Information */}
        <Title level={5}>CashFlow Information</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Name:</Text>
            <br />
            <Text>{cashFlow.name}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Description:</Text>
            <br />
            <Text>{cashFlow.description}</Text>
          </Col>
        </Row>

        <Divider />

        {/* CashFlow Type and Amount */}
        <Title level={5}>CashFlow Details</Title>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Text strong>Type:</Text>
            <br />
            <Text>{cashFlow.type}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Payment Amount:</Text>
            <br />
            <Text>${cashFlow.amount.toLocaleString()}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Date:</Text>
            <br />
            <Text>{cashFlow.date ? dayjs(cashFlow.date).format('YYYY-MM-DD') : 'N/A'}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Account Number:</Text>
            <br />
            <Text>{cashFlow.accountNo || 'N/A'}</Text>
          </Col>
        </Row>

        <Divider />

        <Title level={5}>Recurrence</Title>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Text strong>Recurring:</Text>
            <br />
            <Text>{cashFlow.isRecurring ? 'Yes' : 'No'}</Text>
          </Col>
          {cashFlow.isRecurring && (
            <>
              <Col span={12}>
                <Text strong>Schedule:</Text>
                <br />
                <Text>
                  {describeRecurrence(
                    cashFlow.recurrenceInterval,
                    cashFlow.recurrenceCount,
                    cashFlow.seasonStartMonth,
                    cashFlow.seasonEndMonth,
                  )}
                </Text>
              </Col>
              <Col span={12}>
                <Text strong>Next Due Date:</Text>
                <br />
                <Text>{cashFlow.nextDueDate ? dayjs(cashFlow.nextDueDate).format('YYYY-MM-DD') : 'N/A'}</Text>
              </Col>
            </>
          )}
        </Row>

        <Divider />

        {/* Names, not raw ids: the ids mean nothing to the reader. */}
        <Title level={5}>Linked Entities</Title>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Text strong>Asset:</Text>
            <br />
            <Text>{cashFlow.assetName || 'Not linked'}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Liability:</Text>
            <br />
            <Text>{cashFlow.liabilityName || 'Not linked'}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Participant:</Text>
            <br />
            <Text>{cashFlow.participantName || 'Not linked'}</Text>
          </Col>
        </Row>
      </Space>
    </Modal>
  );
};

export default CashFlowDetails;
